import React from 'react';
import { useState, useEffect } from 'react';
import {
	Grid,
	Autocomplete,
	TextField,
	Typography,
	Switch,
	FormControlLabel,
	Paper,
	ListItem,
} from '@mui/material';
import styles from './IncidentTab.module.css';
import getDispatchCustomers from 'api/getDispatchCustomers';
import getAddresses from 'api/getAddresses';
import { useScheduleContext } from 'containers/pages/CANguard/context/ScheduleProvider';
import { useNotificationProvider } from 'hooks';

export interface Customer {
	Id: number;
	Name: string;
}
export interface Address {
	Id: number;
	Title: string;
}

interface CustomerAddressSelectionProps {
	selectedCustomer: Customer | null;
	selectedAddress: Address | null;
	customerInputError: string | undefined;
	addressInputError: string | undefined;
	handleSelectedCustomerChange: (customer: Customer | null) => void;
	handleSelectedAddressChange: (address: Address | null) => void;
}

const CustomerAddressSelection: React.FC<CustomerAddressSelectionProps> = (
	props,
) => {
	const { schedule } = useScheduleContext();
	const { notifyError } = useNotificationProvider();
	// AUTOCOMPLETE FIELDS:
	const [customers, setCustomers] = useState<Customer[]>([]);
	const [addresses, setAddresses] = useState<Address[]>([]);
	const [loadingCustomers, setLoadingCustomers] = useState<boolean>(false); // Loading state for customers
	const [loadingAddresses, setLoadingAddresses] = useState<boolean>(false); // Loading state for addresses

	// Only show customers that are in the scheduled patrols:
	const [showAllCustomers, setShowAllCustomers] = useState(false);
	const filteredCustomers = showAllCustomers
		? customers
		: customers.filter((customer) =>
				schedule?.some(
					(patrol: ScheduleAssignment) =>
						patrol.cust_id === customer.Id ||
						customer.Name === 'LNN Protection',
				),
		  );

	const handleShowAllCustomersChange = () => {
		setShowAllCustomers((prev) => !prev);
	};

	useEffect(() => {
		(async () => {
			try {
				setLoadingCustomers(true);
				const _companies = await getDispatchCustomers();
				setCustomers(_companies);

				// Setting default customer to LNN Protection:
				const _lnn = _companies.find(
					(company: any) => company.Name === 'LNN Protection',
				);
				props.handleSelectedCustomerChange(_lnn);
			} catch (error) {
				console.error('[CustomerAddressSelection/fetchCustomers] Error fetching customers:', error);
				notifyError('Failed to load customers. Please try again.');
			} finally {
				setLoadingCustomers(false);
			}
		})();
	}, []);

	useEffect(() => {
		(async () => {
			if (loadingAddresses) return;
			try {
				setLoadingAddresses(true);
				setAddresses([]);
				props.handleSelectedAddressChange(null);
				if (props.selectedCustomer) {
					const _addresses = await getAddresses(
						props.selectedCustomer.Id,
					);
					setAddresses(_addresses);
				}
			} catch (error) {
				console.error('[CustomerAddressSelection/fetchAddresses] Error fetching addresses:', error);
				notifyError('Failed to load addresses. Please try again.');
			} finally {
				setLoadingAddresses(false);
			}
		})();
	}, [props.selectedCustomer]);

	return (
		<Grid item className={styles.sectionContainer}>
			<Typography component="div">
				Please select the customer and address associated with this
				incident:
			</Typography>
			<Autocomplete
				key={props.selectedCustomer?.Id}
				value={props.selectedCustomer}
				options={filteredCustomers}
				loading={loadingCustomers}
				loadingText="Loading customers..."
				getOptionLabel={(option) => (option.Name ? option.Name : '')}
				onChange={(e, newValue) => {
					props.handleSelectedCustomerChange(newValue);
				}}
				PaperComponent={(_props) => (
					<Paper {..._props}>
						<ListItem
							onMouseDown={(event) => {
								event.preventDefault();
							}}
							onClick={(event) => {
								event.preventDefault();
								handleShowAllCustomersChange();
							}}
							sx={{
								display: 'flex',
								justifyContent: 'center',
								borderBottom: '1px solid',
								borderColor: 'divider',
							}}
						>
							<FormControlLabel
								label="Show All Customers"
								control={
									<Switch
										checked={showAllCustomers}
										size="medium"
									/>
								}
							/>
						</ListItem>
						{_props.children}
					</Paper>
				)}
				renderInput={(params) => (
					<>
						<TextField
							{...params}
							variant="outlined"
							label="Customers"
							fullWidth
							error={!!props.customerInputError}
							helperText={props.customerInputError}
						/>
					</>
				)}
			/>

			<Autocomplete
				value={props.selectedAddress}
				loading={loadingAddresses}
				loadingText="Loading addresses..."
				options={addresses}
				getOptionLabel={(option) => (option.Title ? option.Title : '')}
				onChange={(e, newValue) => {
					props.handleSelectedAddressChange(newValue);
				}}
				renderOption={(props, option) => (
					<li {...props} key={`${option.Id}-${option.Title}`}>
						{option.Title}
					</li>
				)}
				renderInput={(params) => (
					<TextField
						{...params}
						variant="outlined"
						label="Addresses"
						fullWidth
						error={!!props.addressInputError}
						helperText={props.addressInputError}
					/>
				)}
			/>
		</Grid>
	);
};

export default CustomerAddressSelection;
